import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useToast } from '../ToastSystem';
import {
  buildEventImageFileUrl,
  createEventImageBatch,
  createEventImageFolder,
  deleteEventImages,
  getEventImageSummary,
  getEventImageTree,
  moveEventImages,
} from '../../../api/eventDetails';
import {
  uploadEventImageFile,
} from '../../../api/storage';
import EventImageGalleryView from './EventImageGalleryView';
import {
  ACCEPTED_IMAGE_TYPES,
  createDragPreview,
  getImageDimensions,
  hasExternalFiles,
  isDroppableTransfer,
  loadStoredWatermarkSettings,
  MAX_BATCH_FILES,
  MAX_IMAGE_BYTES,
  renderProcessedImage,
  saveStoredWatermarkSettings,
  UPLOAD_STEPS,
} from './EventImageGallery.helpers';
import '../../../css/common/events/EventImageGallery.css';

function EventImageGallery({
  eventUuid,
  eventName = '',
  eventDateLabel = '',
  canView = true,
  canUpload = false,
  canDownload = false,
  showTitle = true,
}) {
  const { success: toastSuccess, error: toastError } = useToast();
  const fileInputRef = useRef(null);
  const customLogoInputRef = useRef(null);
  const processedPreviewsRef = useRef([]);
  const springFolderTimerRef = useRef(null);
  const springFolderTargetRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null);
  const [folderId, setFolderId] = useState(null);
  const [path, setPath] = useState([]);
  const [folders, setFolders] = useState([]);
  const [images, setImages] = useState([]);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [activeImageId, setActiveImageId] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadStep, setUploadStep] = useState('watermark');
  const [pendingFiles, setPendingFiles] = useState([]);
  const [processedPreviews, setProcessedPreviews] = useState([]);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [sampleOrientation, setSampleOrientation] = useState('landscape');
  const [sampleTone, setSampleTone] = useState('dark');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [dropTargetId, setDropTargetId] = useState(null);
  const [dragPayload, setDragPayload] = useState(null);
  const [uploadTargetFolderId, setUploadTargetFolderId] = useState(null);
  const [settings, setSettings] = useState(() => loadStoredWatermarkSettings());

  const visibleImages = images.slice(0, 10);
  const previewImages = images;
  const usage = summary?.usage || {};
  const limits = summary?.limits || {};
  const selectedCount = selectedIds.size;
  const uploadStepIndex = Math.max(0, UPLOAD_STEPS.findIndex((step) => step.id === uploadStep));
  const activeImageIndex = Math.max(0, previewImages.findIndex((image) => image.id === activeImageId));
  const activeImage = previewImages[activeImageIndex] || previewImages[0] || null;

  const loadData = useCallback(async (nextFolderId = null) => {
    if (!eventUuid || !canView) return;
    setLoading(true);
    try {
      const [nextSummary, tree] = await Promise.all([
        getEventImageSummary(eventUuid),
        getEventImageTree(eventUuid, nextFolderId),
      ]);
      setSummary(nextSummary);
      setFolderId(tree.current_folder_id ?? null);
      setPath(Array.isArray(tree.path) ? tree.path : []);
      setFolders(Array.isArray(tree.folders) ? tree.folders : []);
      setImages(Array.isArray(tree.images) ? tree.images : []);
      setSelectedIds(new Set());
    } catch (error) {
      console.error('Event image load failed:', error);
      toastError(error?.message || 'イベントアルバムの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [canView, eventUuid, toastError]);

  useEffect(() => {
    loadData(null);
  }, [loadData]);

  useEffect(() => {
    saveStoredWatermarkSettings(settings);
  }, [settings]);

  const resetUploadDraft = useCallback(() => {
    setUploadStep('watermark');
    setPendingFiles([]);
    setUploadTargetFolderId(null);
    setProcessedPreviews((current) => {
      current.forEach((item) => URL.revokeObjectURL(item.url));
      return [];
    });
    setPreviewBusy(false);
  }, []);

  const openUploadWizard = useCallback((targetFolderId = folderId) => {
    resetUploadDraft();
    setUploadTargetFolderId(targetFolderId ?? null);
    setUploadOpen(true);
  }, [folderId, resetUploadDraft]);

  useEffect(() => {
    if (!uploadOpen || uploadStep !== 'confirm' || !pendingFiles.length) {
      setProcessedPreviews((current) => {
        current.forEach((item) => URL.revokeObjectURL(item.url));
        return [];
      });
      return undefined;
    }

    let active = true;
    setPreviewBusy(true);
    setProcessedPreviews((current) => {
      current.forEach((item) => URL.revokeObjectURL(item.url));
      return [];
    });

    const buildPreviews = async () => {
      try {
        const previews = [];
        const targets = pendingFiles.slice(0, 1);
        for (const file of targets) {
          const processed = await renderProcessedImage(file, settings, { eventName, eventDate: eventDateLabel });
          const url = URL.createObjectURL(processed.file);
          previews.push({
            name: file.name,
            url,
            fileSize: processed.file.size,
            originalSize: processed.originalSize,
            resized: processed.resized,
          });
        }
        if (!active) {
          previews.forEach((item) => URL.revokeObjectURL(item.url));
          return;
        }
        setProcessedPreviews(previews);
      } catch (error) {
        console.error('Event image preview failed:', error);
        if (active) toastError(error?.message || '加工後プレビューの生成に失敗しました');
      } finally {
        if (active) setPreviewBusy(false);
      }
    };

    buildPreviews();
    return () => {
      active = false;
    };
  }, [eventDateLabel, eventName, pendingFiles, settings, toastError, uploadOpen, uploadStep]);

  useEffect(() => {
    processedPreviewsRef.current = processedPreviews;
  }, [processedPreviews]);

  useEffect(() => () => {
    processedPreviewsRef.current.forEach((item) => URL.revokeObjectURL(item.url));
  }, []);

  const openFolder = useCallback((nextFolderId) => {
    setFolderId(nextFolderId ?? null);
    loadData(nextFolderId ?? null);
  }, [loadData]);

  const clearSpringFolder = useCallback(() => {
    if (springFolderTimerRef.current) {
      window.clearTimeout(springFolderTimerRef.current);
      springFolderTimerRef.current = null;
    }
    springFolderTargetRef.current = null;
  }, []);

  useEffect(() => () => clearSpringFolder(), [clearSpringFolder]);

  const scheduleSpringFolder = useCallback((nextFolderId, targetKey) => {
    if (springFolderTargetRef.current === targetKey) return;
    clearSpringFolder();
    springFolderTargetRef.current = targetKey;
    springFolderTimerRef.current = window.setTimeout(() => {
      springFolderTimerRef.current = null;
      springFolderTargetRef.current = null;
      setDragOver(false);
      setDropTargetId(null);
      openFolder(nextFolderId);
    }, 650);
  }, [clearSpringFolder, openFolder]);

  const toggleSelected = useCallback((id) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const openImagePreview = useCallback((id) => {
    setActiveImageId(id);
    setGalleryOpen(true);
  }, []);

  const moveActiveImage = useCallback((direction) => {
    setActiveImageId((currentId) => {
      if (!previewImages.length) return null;
      const currentIndex = Math.max(0, previewImages.findIndex((image) => image.id === currentId));
      const nextIndex = (currentIndex + direction + previewImages.length) % previewImages.length;
      return previewImages[nextIndex].id;
    });
  }, [previewImages]);

  useEffect(() => {
    if (!galleryOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
        event.preventDefault();
        moveActiveImage(-1);
      }
      if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
        event.preventDefault();
        moveActiveImage(1);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [galleryOpen, moveActiveImage]);

  const moveDraggedImages = useCallback(async (targetFolderId) => {
    const imageIds = dragPayload?.type === 'images' ? dragPayload.imageIds : [];
    if (!imageIds?.length || busy) return;
    const normalizedTarget = targetFolderId ?? null;
    const sourceImages = images.filter((image) => imageIds.includes(image.id));
    if (sourceImages.length && sourceImages.every((image) => (image.folder_id ?? null) === normalizedTarget)) {
      setDragPayload(null);
      setDropTargetId(null);
      return;
    }
    setBusy(true);
    try {
      await moveEventImages(eventUuid, imageIds, normalizedTarget);
      setSelectedIds(new Set());
      await loadData(folderId);
      toastSuccess(imageIds.length === 1 ? '画像を移動しました' : `${imageIds.length}枚の画像を移動しました`);
    } catch (error) {
      toastError(error?.message || 'イベントアルバム画像の移動に失敗しました');
    } finally {
      setBusy(false);
      setDragPayload(null);
      setDropTargetId(null);
      setDragOver(false);
    }
  }, [busy, dragPayload, eventUuid, folderId, images, loadData, toastError, toastSuccess]);

  const startImageDrag = useCallback((event, image) => {
    if (!canUpload || !image?.id) return;
    const imageIds = selectedIds.has(image.id) ? Array.from(selectedIds) : [image.id];
    setDragPayload({ type: 'images', imageIds });
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', imageIds.join(','));
    const preview = createDragPreview({
      count: imageIds.length,
      label: image.file_name || 'イベントアルバム画像',
      className: imageIds.length > 1 ? 'is-batch' : '',
    });
    if (preview) event.dataTransfer.setDragImage(preview, 18, 18);
  }, [canUpload, selectedIds]);

  const createFolder = useCallback(async () => {
    const name = newFolderName.trim();
    if (!name || busy) return;
    setBusy(true);
    try {
      await createEventImageFolder(eventUuid, { name, parent_folder_id: folderId });
      setNewFolderName('');
      setCreatingFolder(false);
      await loadData(folderId);
      toastSuccess('フォルダを作成しました');
    } catch (error) {
      toastError(error?.message || 'フォルダの作成に失敗しました');
    } finally {
      setBusy(false);
    }
  }, [busy, eventUuid, folderId, loadData, newFolderName, toastError, toastSuccess]);

  const handleCustomLogo = useCallback((event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setSettings((current) => ({
        ...current,
        logoEnabled: true,
        logoPreset: 'custom',
        customLogoDataUrl: String(reader.result || ''),
      }));
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  }, []);

  const handleSelectedFiles = useCallback((fileList) => {
    const candidates = Array.from(fileList || []).filter((file) => ACCEPTED_IMAGE_TYPES.has(file.type));
    if (!candidates.length) {
      toastError('JPEG、PNG、WebP の画像を選択してください');
      return false;
    }
    if (candidates.length > MAX_BATCH_FILES) {
      toastError(`一度に追加できるイベントアルバム画像は${MAX_BATCH_FILES}枚までです`);
      return false;
    }
    setPendingFiles(candidates);
    setUploadStep('confirm');
    return true;
  }, [toastError]);

  const handleExternalFilesDrop = useCallback((fileList, targetFolderId = folderId) => {
    if (!canUpload) return;
    setUploadTargetFolderId(targetFolderId ?? null);
    if (handleSelectedFiles(fileList)) {
      setUploadOpen(true);
    }
  }, [canUpload, folderId, handleSelectedFiles]);

  const handleGalleryDragOver = useCallback((event, targetId = 'root', springFolderId = undefined) => {
    if (!canUpload || uploadOpen || galleryOpen || !isDroppableTransfer(event.dataTransfer, dragPayload)) return;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = dragPayload ? 'move' : 'copy';
    setDragOver(targetId === 'root');
    setDropTargetId(targetId);
    if (springFolderId === undefined) {
      clearSpringFolder();
    } else {
      scheduleSpringFolder(springFolderId, targetId);
    }
  }, [canUpload, clearSpringFolder, dragPayload, galleryOpen, scheduleSpringFolder, uploadOpen]);

  const handleGalleryDrop = useCallback((event, targetFolderId = folderId) => {
    if (!canUpload || uploadOpen || galleryOpen) return;
    if (!isDroppableTransfer(event.dataTransfer, dragPayload)) return;
    event.preventDefault();
    event.stopPropagation();
    clearSpringFolder();
    setDragOver(false);
    setDropTargetId(null);
    if (event.dataTransfer?.files?.length) {
      handleExternalFilesDrop(event.dataTransfer.files, targetFolderId);
      return;
    }
    moveDraggedImages(targetFolderId ?? null);
  }, [canUpload, clearSpringFolder, dragPayload, folderId, galleryOpen, handleExternalFilesDrop, moveDraggedImages, uploadOpen]);

  const uploadPendingFiles = useCallback(async () => {
    const candidates = pendingFiles;
    if (!candidates.length) {
      setUploadStep('select');
      return;
    }
    const targetFolderId = uploadTargetFolderId ?? folderId ?? null;
    setBusy(true);
    try {
      const uploads = [];
      for (let index = 0; index < candidates.length; index += 1) {
        const sourceFile = candidates[index];
        const processed = sourceFile.size > MAX_IMAGE_BYTES || settings.logoEnabled || settings.textEnabled
          ? await renderProcessedImage(sourceFile, settings, { eventName, eventDate: eventDateLabel })
          : {
              file: sourceFile,
              ...(await getImageDimensions(sourceFile)),
              resized: false,
              originalSize: sourceFile.size,
            };
        if (processed.file.size > MAX_IMAGE_BYTES) {
          throw new Error(`${sourceFile.name} を20MB以下にできませんでした。`);
        }
        const uploadedFile = await uploadEventImageFile({
          eventUuid,
          file: processed.file,
        });
        uploads.push({
          file_name: processed.file.name,
          original_file_name: sourceFile.name,
          file_key: uploadedFile.key,
          file_size: processed.file.size,
          content_type: processed.file.type,
          width: processed.width,
          height: processed.height,
          sort_order: index,
          watermark_layers: [
            {
              logoPreset: settings.logoPreset,
              logoEnabled: settings.logoEnabled,
              logoFillColor: settings.logoFillColor,
              customLogoFillColor: settings.logoFillColor === 'custom' ? settings.customLogoFillColor : '',
              textPreset: settings.textPreset,
              textEnabled: settings.textEnabled,
              logoPosition: settings.logoPosition,
              textPosition: settings.textPosition,
              opacity: settings.opacity,
            },
          ],
          client_processing_summary: {
            original_size: processed.originalSize,
            resized: processed.resized,
          },
        });
      }
      await createEventImageBatch(eventUuid, {
        folder_id: targetFolderId,
        idempotency_key: `event-images:${eventUuid}:${Date.now()}:${Math.random().toString(36).slice(2)}`,
        images: uploads,
        watermark_preset_snapshot: {
          ...settings,
          customLogoDataUrl: settings.customLogoDataUrl ? '[custom-image]' : '',
        },
      });
      setUploadOpen(false);
      resetUploadDraft();
      await loadData(folderId);
      toastSuccess('イベントアルバムへ追加しました');
    } catch (error) {
      console.error('Event image upload failed:', error);
      toastError(error?.message || 'イベントアルバムへの追加に失敗しました');
    } finally {
      setBusy(false);
    }
  }, [eventDateLabel, eventName, eventUuid, folderId, loadData, pendingFiles, resetUploadDraft, settings, toastError, toastSuccess, uploadTargetFolderId]);

  const downloadSelected = useCallback(() => {
    if (!selectedIds.size) return;
    Array.from(selectedIds).forEach((id, index) => {
      window.setTimeout(() => window.open(buildEventImageFileUrl(eventUuid, id, { download: true }), '_blank', 'noopener'), index * 120);
    });
  }, [eventUuid, selectedIds]);

  const deleteSelected = useCallback(async () => {
    if (!canUpload || !selectedIds.size || busy) return;
    setBusy(true);
    try {
      await deleteEventImages(eventUuid, Array.from(selectedIds));
      setDeleteOpen(false);
      setSelectedIds(new Set());
      await loadData(folderId);
      toastSuccess('選択した画像を削除しました');
    } catch (error) {
      toastError(error?.message || 'イベントアルバム画像の削除に失敗しました');
    } finally {
      setBusy(false);
    }
  }, [busy, canUpload, eventUuid, folderId, loadData, selectedIds, toastError, toastSuccess]);

  const selectedAllInView = useMemo(
    () => previewImages.length > 0 && previewImages.every((image) => selectedIds.has(image.id)),
    [previewImages, selectedIds]
  );

  if (!canView) return null;

  return (
    <EventImageGalleryView
      activeImage={activeImage}
      activeImageIndex={activeImageIndex}
      buildEventImageFileUrl={buildEventImageFileUrl}
      busy={busy}
      canDownload={canDownload}
      canUpload={canUpload}
      clearSpringFolder={clearSpringFolder}
      createFolder={createFolder}
      creatingFolder={creatingFolder}
      customLogoInputRef={customLogoInputRef}
      deleteOpen={deleteOpen}
      deleteSelected={deleteSelected}
      downloadSelected={downloadSelected}
      dragOver={dragOver}
      dropTargetId={dropTargetId}
      eventDateLabel={eventDateLabel}
      eventName={eventName}
      eventUuid={eventUuid}
      fileInputRef={fileInputRef}
      folderId={folderId}
      folders={folders}
      galleryOpen={galleryOpen}
      handleCustomLogo={handleCustomLogo}
      handleGalleryDragOver={handleGalleryDragOver}
      handleGalleryDrop={handleGalleryDrop}
      handleSelectedFiles={handleSelectedFiles}
      hasExternalFiles={hasExternalFiles}
      images={images}
      limits={limits}
      loading={loading}
      moveActiveImage={moveActiveImage}
      newFolderName={newFolderName}
      openFolder={openFolder}
      openImagePreview={openImagePreview}
      openUploadWizard={openUploadWizard}
      path={path}
      pendingFiles={pendingFiles}
      previewBusy={previewBusy}
      previewImages={previewImages}
      processedPreviews={processedPreviews}
      resetUploadDraft={resetUploadDraft}
      sampleOrientation={sampleOrientation}
      sampleTone={sampleTone}
      selectedAllInView={selectedAllInView}
      selectedCount={selectedCount}
      selectedIds={selectedIds}
      showTitle={showTitle}
      setActiveImageId={setActiveImageId}
      setCreatingFolder={setCreatingFolder}
      setDeleteOpen={setDeleteOpen}
      setDragOver={setDragOver}
      setDragPayload={setDragPayload}
      setDropTargetId={setDropTargetId}
      setGalleryOpen={setGalleryOpen}
      setNewFolderName={setNewFolderName}
      setSampleOrientation={setSampleOrientation}
      setSampleTone={setSampleTone}
      setSelectedIds={setSelectedIds}
      setSettings={setSettings}
      setUploadOpen={setUploadOpen}
      setUploadStep={setUploadStep}
      setUploadTargetFolderId={setUploadTargetFolderId}
      settings={settings}
      startImageDrag={startImageDrag}
      toggleSelected={toggleSelected}
      uploadOpen={uploadOpen}
      uploadPendingFiles={uploadPendingFiles}
      uploadStep={uploadStep}
      uploadStepIndex={uploadStepIndex}
      usage={usage}
      visibleImages={visibleImages}
    />
  );
}

export default EventImageGallery;
